import { CheckoutContext } from '@/context/CheckoutContext';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RedirectPaymentManager } from '@/services/redirectPaymentManager';
import { notifyPaymentIntentFailed, notifyPaymentIntentSuccess } from '@/utils/notifier';
import { PaymentMethodEnum } from '@/types/common';
import { isMobileDevice } from '@/utils/device';
import { CashAppRedirect } from './CashAppRedirect';
import { useGetStatus } from '@/api/status';
import { useTranslate } from '@/hooks/useTranslate';
import { ErrorLogs, InfoLogs } from '@/constants/logs';
import { logError, logInfo } from '@/utils/logger';
import { getEventDataForSdkEvents } from '@/utils/eventDataBuilder';
import { useCheckoutData } from '@/api/useCheckoutData';

export const RedirectPayment = () => {
  const context = useContext(CheckoutContext);
  const checkoutData = useCheckoutData({ enableFetch: false });
  const {
    redirectPayment,
    actions: { setIsInPaymentProcess, setRedirectPayment, setSuccessScreen, setError },
    paymentMethodSelected,
    otherMethodSelected,
    paymentMethodsSettings,
  } = context;
  const { redirectUrl, provider, clientSecret, handlePaymentSuccess, handlePaymentError, openedTab } = redirectPayment;
  const selectedPaymentMethod = useMemo(
    () => (paymentMethodSelected === PaymentMethodEnum.OTHER ? otherMethodSelected : paymentMethodSelected),
    [paymentMethodSelected, otherMethodSelected]
  );
  const paymentMethodId = useMemo(
    () =>
      paymentMethodsSettings.find(
        (method) => method.paymentMethodName === selectedPaymentMethod && method.provider === provider
      )?.id ?? '',
    [paymentMethodsSettings, selectedPaymentMethod, provider]
  );
  const { mutateAsync: getStatus } = useGetStatus();
  const managerRef = useRef<RedirectPaymentManager | null>(null);
  const tabIsOpenedRef = useRef(false);
  const [isProcessing] = useState(true);
  const { t } = useTranslate();

  useEffect(() => {
    if (
      !redirectUrl ||
      !provider ||
      tabIsOpenedRef.current ||
      (selectedPaymentMethod === PaymentMethodEnum.CASH_APP && !isMobileDevice())
    )
      return;

    logInfo(InfoLogs.checkoutPaymentRedirected, {
      provider,
      redirectUrl,
    });
    const manager = new RedirectPaymentManager(provider);
    managerRef.current = manager;
    if (openedTab) {
      manager.setTab(openedTab);
      manager.sendRedirectMessage(redirectUrl);
    } else {
      manager.openTab(redirectUrl);
    }
    tabIsOpenedRef.current = true;

    if (clientSecret || paymentMethodId) {
      manager.startPolling(
        clientSecret ?? '',
        (params: Record<string, unknown>) => {
          logInfo(InfoLogs.checkoutPaymentSuccess, {
            params,
            redirectUrl,
          });
          notifyPaymentIntentSuccess(getEventDataForSdkEvents(checkoutData.data!));
          setRedirectPayment({});
          setSuccessScreen({ isOpen: true, paymentDetails: { params }, provider });
          handlePaymentSuccess?.();
        },
        (_, params: Record<string, unknown>) => {
          logError(ErrorLogs.checkoutPaymentError, {
            params,
            redirectUrl,
          });
          notifyPaymentIntentFailed(checkoutData.data!);
          setError(t('somethingWentWrong'));
          setIsInPaymentProcess(false);
          setRedirectPayment({});
          handlePaymentError?.();
        },
        async (redirectResult: string) => await getStatus({ paymentMethodId, redirectResult }),
        paymentMethodId
      );
    }
  }, [
    redirectUrl,
    provider,
    clientSecret,
    setRedirectPayment,
    setIsInPaymentProcess,
    setSuccessScreen,
    setError,
    handlePaymentSuccess,
    handlePaymentError,
    selectedPaymentMethod,
    paymentMethodId,
    getStatus,
  ]);

  if (selectedPaymentMethod === PaymentMethodEnum.CASH_APP && !isMobileDevice()) {
    return <CashAppRedirect />;
  }
  if (!isProcessing) return null;

  return <PaymentProcessOverlay />;
};

const PaymentProcessOverlay = () => {
  const { t } = useTranslate();
  return (
    <div className="fixed inset-0 bg-base-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-[var(--radius-xl)] p-8 shadow-xl">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue" />
          <p className="text-lg font-medium text-card-foreground">{t('processing')}</p>
          <p className="text-sm text-muted-foreground">{t('pleaseCompletePaymentInWindow')}</p>
        </div>
      </div>
    </div>
  );
};
